import React, { useEffect, useMemo, useState } from "react";
import { FaRobot, FaPlay, FaStop, FaSpinner } from "react-icons/fa";
import {AIStatus,  hasAIAccess, toggleAI} from "../services/AIServices";
import toast from "react-hot-toast";

const POLL_INTERVAL_MS = 30000; // 30s

const AutoRunBotIcon = () => {
    const [status, setStatus] = useState(null); // { success, data: { running, minutesRemaining, lastPrediction, ... } }
    const [hasAccess, setHasAccess] = useState(null); // null unknown, true/false known
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchStatus = async () => {
        try {
            const res = await AIStatus();
            setStatus(res);
            setError("");
        } catch (e) {
            console.error(e);
            setError("Failed to fetch AI status");
        }
    };

    const checkAccess = async () => {
        try {
            const res = await hasAIAccess();
            const allowed = !!res?.data?.hasAccess;
            setHasAccess(allowed);
            if (!allowed) {
                toast.error(res?.message || "You do not have access to start AI");
            }
        } catch (e) {
            console.error(e);
            setHasAccess(false);
            toast.error("Unable to verify AI access");
        }
    };

    useEffect(() => {
        // Initial fetches
        checkAccess();
        fetchStatus();
        const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const running = status?.data?.running === true;
    const minutesRemaining = status?.data?.minutesRemaining ?? null;
    const lastPrediction = status?.data?.lastPrediction ?? null;

    const direction = lastPrediction?.direction || (lastPrediction?.up > lastPrediction?.down ? "UP" : "DOWN");
    const upPct = useMemo(() => lastPrediction ? Math.round((lastPrediction.up ?? 0) * 100) : null, [lastPrediction]);
    const downPct = useMemo(() => lastPrediction ? Math.round((lastPrediction.down ?? 0) * 100) : null, [lastPrediction]);

    const onStart = async () => {
        // Gate by access first
        if (hasAccess === false) {
            toast.error("You do not have access to start AI");
            return;
        }
        if (hasAccess === null) {
            await checkAccess();
            if (hasAccess === false) return;
        }

        setLoading(true);
        try {
            const res = await toggleAI();
            if (res?.success) {
                toast.success(res?.message || "AI started");
            } else {
                toast.error(res?.message || "Failed to start AI");
            }
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Failed to start AI";
            toast.error(msg);
        } finally {
            await fetchStatus();
            setLoading(false);
        }
    };

    const onStop = async () => {
        setLoading(true);
        try {
            const res = await toggleAI();
            if (res?.success) {
                toast.success(res?.message || "AI stopped");
            } else {
                toast.error(res?.message || "Failed to stop AI");
            }
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Failed to stop AI";
            toast.error(msg);
        } finally {
            await fetchStatus();
            setLoading(false);
        }
    };

    const disabledNoAccess = hasAccess === false;
    const buttonDisabled = loading || disabledNoAccess;

    return (
        <div className="relative flex flex-col items-center">
            {/* Glowing ring wrapper */}
            <div className={`p-[2px] rounded-full ${running ? "bg-gradient-to-r from-green-400/60 via-emerald-400/60 to-teal-400/60" : "bg-gradient-to-r from-indigo-400/40 via-purple-400/40 to-pink-400/40"} shadow-[0_0_30px_-10px_rgba(0,0,0,0.5)]`}>
                {/* Button */}
                {running ? (
                    <button
                        onClick={onStop}
                        disabled={buttonDisabled}
                        className={`group relative flex items-center justify-center w-36 h-12 rounded-full bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white shadow-lg hover:shadow-green-500/20 transition-all ${buttonDisabled ? "opacity-60 cursor-not-allowed" : "hover:scale-[1.02] active:scale-[0.99]"}`}
                        title={disabledNoAccess ? "You do not have access to stop AI" : `AI is running${minutesRemaining !== null ? ` • ${minutesRemaining}m left` : ""}`}
                    >
                        <span className="absolute -left-1.5 -top-1.5 inline-flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                        </span>
                        {loading ? (
                            <FaSpinner className="mr-2 animate-spin" />
                        ) : (
                            <FaStop className="mr-2" />
                        )}
                        Stop AI
                    </button>
                ) : (
                    <button
                        onClick={onStart}
                        disabled={buttonDisabled}
                        className={`group relative flex items-center justify-center w-36 h-12 rounded-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white shadow-lg hover:shadow-fuchsia-500/20 transition-all ${buttonDisabled ? "opacity-60 cursor-not-allowed" : "hover:scale-[1.02] active:scale-[0.99]"}`}
                        title={disabledNoAccess ? "You do not have access to start AI" : "Start AI trading bot"}
                    >
                        <span className="absolute -left-1.5 -top-1.5 inline-flex h-3 w-3">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-50 group-hover:animate-ping" />
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500" />
                        </span>
                        {loading ? (
                            <FaSpinner className="mr-2 animate-spin" />
                        ) : (
                            <FaPlay className="mr-2" />
                        )}
                        Start AI
                    </button>
                )}
            </div>

            {/* Status chip */}
            <div className="mt-3 flex items-center text-xs"> 
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${running ? "border-green-500/50 bg-green-500/10 text-green-300" : "border-gray-500/40 bg-gray-500/10 text-gray-300"}`}>
                    <FaRobot className={running ? "text-green-400" : "text-gray-400"} />
                    {running ? "Running" : "Idle"}
                </span>
                {typeof minutesRemaining === 'number' && (
                    <span className="ml-2 text-gray-400">• {minutesRemaining}m left</span>
                )}
                {disabledNoAccess && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border border-red-500/40 bg-red-500/10 text-red-300">No Access</span>
                )}
            </div>

            {/* Prediction card */}
            {lastPrediction && (
                <div className="mt-3 w-56 rounded-xl border border-white/5 bg-gradient-to-br from-gray-800/70 to-gray-900/70 backdrop-blur-sm p-3 shadow-inner">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-wider text-gray-400">Last Prediction</div>
                        <div className={`text-xs font-semibold ${direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>{direction}</div>
                    </div>

                    {/* Progress bars */}
                    <div className="space-y-2">
                        <div>
                            <div className="flex justify-between text-[11px] text-gray-400 mb-1">
                                <span>UP</span>
                                <span className="text-green-300">{upPct}%</span>
                            </div>
                            <div className="h-2.5 w-full bg-gray-700/60 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400" style={{ width: `${Math.max(0, Math.min(100, upPct ?? 0))}%` }} />
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-[11px] text-gray-400 mb-1">
                                <span>DOWN</span>
                                <span className="text-red-300">{downPct}%</span>
                            </div>
                            <div className="h-2.5 w-full bg-gray-700/60 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-rose-500 to-red-400" style={{ width: `${Math.max(0, Math.min(100, downPct ?? 0))}%` }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error */}
            {error && (
                <span className="mt-2 text-[11px] text-red-400">{error}</span>
            )}
        </div>
    );
};

export default AutoRunBotIcon;
